import { automationTypeValues } from '@pages/roboto-calls/utils/constants/call-collection';
import {
  CollectionAutomationType,
  CollectionCheckType,
  CollectionDataType,
  CollectionDayOfWeekType,
  CollectionFieldType,
  CollectionMediaFileType,
  CollectionOneCallDataType
} from '@pages/roboto-calls/utils/models/call-collection';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { DATE_FORMAT } from '@utils/constants';
import dayjs from 'dayjs';
import { uid } from 'uid';

export type RobotoCallSliceType = {
  id?: number | null;
  collectionVisible: boolean;

  audience: {
    id?: number;
    name: string;
    dataType: CollectionDataType;
    queries: {
      uid: string;
      id?: number;
      date?: string | null;
      value?: string | null;
      ids?: string[] | null;
      subIds?: string[] | null;
      field: CollectionFieldType | null;
      check: CollectionCheckType | null;
    }[];
  };

  planning: {
    id?: number | null;
    channel: number | null;
    startTime: string | null;
    startDate: string | null;
    retryCount: number | null;
    finishDate: string | null;
    scheduleDate: string | null;
    repeatEveryDay: number | null;
    visibleOtherAutomation: boolean;
    channelsParallelCallCount: number | null;
    dayOfWeeks: CollectionDayOfWeekType[] | null;
    automationType: CollectionAutomationType | null;
  };

  media: {
    fileList:
      | {
          order: number;
          speed?: number;
          text?: string;
          voice?: string;
          type?: string;
          files: CollectionMediaFileType | null;
        }[]
      | null;
  };

  action: {
    id: number;
    visible: boolean;
    type: 'delete' | 'cancel' | 'deleteScript';
  };

  speechKitVisible: { visible: boolean; order: number };
  speechKitText: string;
  totalElements: number | null;
  statusSpeechKit: boolean;
  chatFlowId: number | null;
};

const initialState: RobotoCallSliceType = {
  collectionVisible: false,

  audience: {
    name: '',
    queries: [
      {
        uid: uid(),
        check: null,
        field: null
      }
    ],
    dataType: 'DEBT'
  },

  planning: {
    channel: null,
    retryCount: 1,
    startTime: null,
    startDate: null,
    finishDate: null,
    dayOfWeeks: null,
    scheduleDate: null,
    repeatEveryDay: null,
    automationType: 'NOW',
    channelsParallelCallCount: 1,
    visibleOtherAutomation: false
  },

  media: {
    fileList: null
  },

  action: {
    id: 0,
    visible: false,
    type: 'delete'
  },
  speechKitVisible: { visible: false, order: 0 },
  speechKitText: '',
  totalElements: null,
  statusSpeechKit: false,
  chatFlowId: null
};

const robotoCallSlice = createSlice({
  initialState,
  name: 'roboto-call-slice',
  reducers: {
    // collection
    setActionCollection: (state, action: PayloadAction<{ visible: boolean; data?: CollectionOneCallDataType }>) => {
      const { visible, data } = action.payload;

      state.collectionVisible = visible;

      if (data) {
        state.id = data?.id || null;

        state.audience = {
          name: data?.name,
          id: data?.query?.id,
          dataType: data?.query?.dataType,
          queries: data?.query?.filter?.queries?.map(item => ({
            uid: uid(),
            ...item
          }))
        };

        state.planning = {
          ...data?.campaignAutomation,
          channel: data?.channel?.id,
          retryCount: data?.retryCount,
          visibleOtherAutomation: false,
          channelsParallelCallCount: data?.channelsParallelCallCount
        };

        state.media = {
          fileList: data?.audios?.map(({ id, order, files, speed, text, type, voice }) => ({
            id,
            order,
            speed,
            text,
            type,
            voice,
            files
          }))
        };
      }
    },
    setClearCollection: state => {
      state.id = null;
      state.media = initialState.media;
      state.audience = initialState.audience;
      state.planning = initialState.planning;
    },
    setCollectionId: (state, action: PayloadAction<number>) => {
      state.id = action.payload;
    },

    // audience
    setCollectionAudienceItem: <TKey extends keyof RobotoCallSliceType['audience']>(
      state: RobotoCallSliceType,
      actions: PayloadAction<{
        key: TKey;
        value: RobotoCallSliceType['audience'][TKey];
      }>
    ) => {
      const { key, value } = actions.payload;

      state.audience[key] = value;

      if (key === 'dataType') {
        state.audience.queries = [
          {
            uid: uid(),
            check: null,
            field: null
          }
        ];
      }
    },
    setCollectionAudienceQueriesItem: <TKey extends keyof RobotoCallSliceType['audience']['queries'][number]>(
      state: RobotoCallSliceType,
      actions: PayloadAction<{
        key: TKey;
        index: number;
        value: RobotoCallSliceType['audience']['queries'][number][TKey];
      }>
    ) => {
      const { key, value, index } = actions.payload;

      state.audience.queries[index][key] = value;

      if (key === 'field') {
        state.audience.queries[index].ids = null;
        state.audience.queries[index].date = null;
        state.audience.queries[index].check = null;
        state.audience.queries[index].value = null;
        state.audience.queries[index].subIds = null;
      }
    },
    setCollectionAudienceQueryDelete: (state, action: PayloadAction<string>) => {
      state.audience.queries = state.audience.queries?.filter(({ uid }) => uid !== action.payload);
    },
    setCollectionAudienceQueryAdd: state => {
      state.audience.queries = [
        ...state.audience.queries,
        {
          uid: uid(),
          check: null,
          field: null
        }
      ];
    },

    // planning
    setCollectionPlanningItem: <TKey extends keyof RobotoCallSliceType['planning']>(
      state: RobotoCallSliceType,
      actions: PayloadAction<{
        key: TKey;
        value: RobotoCallSliceType['planning'][TKey];
      }>
    ) => {
      const { key, value } = actions.payload;

      state.planning[key] = value;

      if (
        key === 'automationType' &&
        value &&
        ![
          'OTHER',
          automationTypeValues.EVERY_DAY,
          automationTypeValues.CUSTOM_DAILY,
          automationTypeValues.CUSTOM_WEEKLY
        ].includes(value as string)
      ) {
        state.planning.finishDate = null;
        state.planning.startDate = null;
      }

      if (key === 'automationType' && value === 'OTHER') {
        state.planning.visibleOtherAutomation = true;
      }

      if (key === 'automationType' && value !== automationTypeValues.SCHEDULE) {
        state.planning.scheduleDate = null;
      }

      if (
        key === 'automationType' &&
        value &&
        ![automationTypeValues.CUSTOM_DAILY, automationTypeValues.CUSTOM_WEEKLY].includes(value as string)
      ) {
        state.planning.dayOfWeeks = null;
        state.planning.repeatEveryDay = null;
      }

      if (
        key === 'automationType' &&
        value &&
        [
          'OTHER',
          automationTypeValues.EVERY_DAY,
          automationTypeValues.CUSTOM_DAILY,
          automationTypeValues.CUSTOM_WEEKLY
        ].includes(value as string) &&
        !state.planning.startDate
      ) {
        state.planning.startDate = dayjs().format(DATE_FORMAT);
      }
    },

    // media
    setCollectionMediaFile: (state, actions: PayloadAction<{ files: CollectionMediaFileType }>) => {
      state.media.fileList = [
        ...(state.media.fileList || []),
        { files: actions.payload?.files, type: 'AUDIO', order: (state.media.fileList?.length || 0) + 1 }
      ];
    },
    setCollectionSpeechKitFile: (
      state,
      actions: PayloadAction<{
        order: number;
        speed?: number;
        text?: string;
        voice?: string;
        type?: string;
        files: CollectionMediaFileType | null;
      }>
    ) => {
      state.media.fileList = [
        ...(state.media.fileList || []),
        { ...actions.payload, type: 'YANDEX_TTS', order: (state.media.fileList?.length || 0) + 1 }
      ];
    },
    setCollectionMediaFileDelete: (state, action: PayloadAction<number>) => {
      state.media.fileList = state.media?.fileList?.filter(item => item?.files?.id !== action.payload) || null;
    },
    setCollectionFileDelete: (state, action: PayloadAction<number>) => {
      state.media.fileList = state.media?.fileList?.filter(item => item?.order !== action.payload) || null;
    },

    setAllCollectionMediaFiles: (
      state,
      action: PayloadAction<
        | {
            order: number;
            speed?: number;
            text?: string;
            voice?: string;
            type?: string;
            files: CollectionMediaFileType | null;
          }[]
        | null
      >
    ) => {
      state.media.fileList = action.payload;
    },

    // action
    setCollectionAction: (state, actions: PayloadAction<RobotoCallSliceType['action']>) => {
      state.action = actions.payload;
    },
    setSpeechKitVisible: (state, actions: PayloadAction<{ order: number; visible: boolean }>) => {
      state.speechKitVisible.order = actions.payload.order;
      state.speechKitVisible.visible = actions.payload.visible;
    },
    setSpeechKitText: (state, action: PayloadAction<string>) => {
      state.speechKitText = action.payload;
    },
    setTotalElements: (state, action: PayloadAction<number | null>) => {
      state.totalElements = action.payload;
    },
    setStatusSpeechKit: (state, action: PayloadAction<boolean>) => {
      state.statusSpeechKit = action.payload;
    },
    setChatFlowId: (state, action: PayloadAction<number | null>) => {
      state.chatFlowId = action.payload;
    }
  }
});

export const robotoCallReducer = robotoCallSlice.reducer;
export const {
  setClearCollection,
  setCollectionAction,
  setActionCollection,
  setCollectionMediaFile,
  setCollectionAudienceItem,
  setCollectionPlanningItem,
  setCollectionMediaFileDelete,
  setCollectionAudienceQueryAdd,
  setCollectionAudienceQueriesItem,
  setCollectionAudienceQueryDelete,
  setSpeechKitVisible,
  setSpeechKitText,
  setCollectionSpeechKitFile,
  setCollectionFileDelete,
  setAllCollectionMediaFiles,
  setTotalElements,
  setStatusSpeechKit,
  setChatFlowId
} = robotoCallSlice.actions;
